import axios, { AxiosRequestConfig } from 'axios';
import { BASE_API_URL_USER, SERVICE_LOGIN } from '../../app_constants';

// --- ТИПЫ И ИНТЕРФЕЙСЫ ОТВЕТОВ ---

export interface ICheckUserResponse {
  exist: boolean;
  login_hint: string;
}

export interface IVerifyCodeResponse {
  result: boolean;
  login_hint: string;
  key: string;
}

export interface ITokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  token_type: string;
}

class Service {
  private static async hashToken(token: string): Promise<string> {
    const msgBuffer = new TextEncoder().encode(token);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  private static async getAuthHeader(): Promise<string> {
    const challengeResponse = await axios.get(`${BASE_API_URL_USER}registration/challenge`);
    const token = challengeResponse.data.challenge;

    if (!token) {
      throw new Error('Не удалось получить challenge для авторизации');
    }

    const clientHash = await Service.hashToken(token);
    const authString = `${SERVICE_LOGIN}:${clientHash}`;
    return `Basic ${btoa(authString)}`;
  }

  private static getBaseConfig(
    authHeader: string,
    params?: Record<string, string>,
  ): AxiosRequestConfig {
    return {
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader,
      },
      params,
      validateStatus: (status) => status < 500,
    };
  }

  private static handleError(error: unknown, defaultMessage: string): never {
    if (axios.isAxiosError(error)) {
      console.error(`${defaultMessage}:`, error.message);

      let errorMessage = 'Произошла сетевая ошибка.';

      if (error.response?.data) {
        errorMessage =
          typeof error.response.data === 'string'
            ? error.response.data
            : JSON.stringify(error.response.data);
      }

      throw new Error(errorMessage);
    }

    console.error('Неизвестная ошибка:', error);
    throw error;
  }

  // ==========================================
  // 2. МОДУЛЬ РАБОТЫ С КОДАМИ ВЕРИФИКАЦИИ (OTP)
  // ==========================================

  static async requestCode(phone: string): Promise<boolean> {
    try {
      const authHeader = await Service.getAuthHeader();
      const config = Service.getBaseConfig(authHeader, { phone });

      const response = await axios.get(`${BASE_API_URL_USER}registration/code`, config);

      if (response.status === 400) {
        throw new Error(response.data as string);
      }

      return response.data;
    } catch (error) {
      return Service.handleError(error, 'Ошибка при запросе кода подтверждения');
    }
  }

  static async verifyCode(phone: string, code: string): Promise<IVerifyCodeResponse> {
    try {
      const authHeader = await Service.getAuthHeader();
      const config = Service.getBaseConfig(authHeader);

      const response = await axios.post(
        `${BASE_API_URL_USER}registration/code`,
        { phone, code },
        config,
      );

      if (response.status === 400) {
        throw new Error(response.data as string);
      }

      return response.data;
    } catch (error) {
      return Service.handleError(error, 'Ошибка при проверке кода');
    }
  }

  // ==========================================
  // 3. МОДУЛЬ УПРАВЛЕНИЯ АККАУНТОМ
  // ==========================================

  static async checkUserExists(phone: string): Promise<ICheckUserResponse> {
    try {
      const authHeader = await Service.getAuthHeader();
      const config = Service.getBaseConfig(authHeader, { phone });

      const response = await axios.get(`${BASE_API_URL_USER}registration/auth`, config);

      if (response.status === 400) {
        throw new Error(response.data as string);
      }

      return response.data;
    } catch (error) {
      return Service.handleError(error, 'Ошибка при проверке существования пользователя');
    }
  }

  static async createUser(key: string): Promise<ICheckUserResponse> {
    try {
      const authHeader = await Service.getAuthHeader();
      const config = Service.getBaseConfig(authHeader);

      const response = await axios.post(`${BASE_API_URL_USER}registration/auth`, { key }, config);

      if (response.status === 400) {
        throw new Error(response.data as string);
      }

      return response.data;
    } catch (error) {
      return Service.handleError(error, 'Ошибка при создании пользователя');
    }
  }

  static async updateUserPassword(key: string): Promise<boolean> {
    try {
      const authHeader = await Service.getAuthHeader();
      const config = Service.getBaseConfig(authHeader);

      const response = await axios.put(`${BASE_API_URL_USER}registration/auth`, { key }, config);

      if (response.status === 400) {
        throw new Error(response.data as string);
      }

      return response.data;
    } catch (error) {
      return Service.handleError(error, 'Ошибка при изменении пароля');
    }
  }

  static async getTokens(key: string): Promise<ITokenResponse> {
    try {
      const authHeader = await Service.getAuthHeader();
      const config = Service.getBaseConfig(authHeader);

      const response = await axios.post(`${BASE_API_URL_USER}registration/token`, { key }, config);

      if (response.status === 400) {
        throw new Error(response.data as string);
      }

      return response.data;
    } catch (error) {
      return Service.handleError(error, 'Ошибка при получении токенов');
    }
  }
}

export default Service;
